import { Stethoscope, CalendarDays, ClipboardList, ClipboardPen } from 'lucide-react';
import { vaOr, BB_DOC } from './vaOr';

/*
 * VA Chronic Pain rotation. Built from the Blackboard VA Chronic Pain Clinic Orientation
 * Guide; the exam templates are linked. See docs/blackboard-va.md.
 * Contacts, Locations and Documents extend the General OR rotation.
 */

const PAIN = BB_DOC('_260917_1');
const TEMPLATES = BB_DOC('_258833_1');

export const vaPain = {
  accent: vaOr.accent,
  icon: Stethoscope,
  tagline: 'Clinic and procedure days, CPRS consults, iMed consents',
  notesKey: 'notes:va:pain',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'clinic', title: 'Clinic & OR Days', subtitle: 'Seeing patients, presenting, consents, orders', icon: ClipboardList, action: 'guide:clinic', featured: true },
      { id: 'charting', title: 'Charting & Consents', subtitle: 'Billing codes, iMed consents, delayed orders', icon: ClipboardPen, action: 'guide:charting' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Week', subtitle: 'Day one, the weekly schedule', icon: CalendarDays, action: 'guide:day' },
    ] },
  ],

  locations: {
    groups: [
      { id: 'pain', title: 'Chronic pain', items: [
        { name: 'Chronic pain clinic', floor: 'Surgery clinic', note: 'Green elevators' },
        { name: 'Day one meeting point', floor: '5C-222', where: 'Green elevator', note: 'Sean Nguyen, clinic RN, at 07:00' },
        { name: 'Procedure day preop', floor: 'SACC, 5H', note: 'First patient at 07:00' },
      ] },
      ...vaOr.locations.groups,
    ],
  },

  documents: {
    storageKey: 'va-pain-pinned-docs',
    groups: [
      { id: 'pain', title: 'Chronic pain', items: [
        { title: 'Chronic pain orientation', subtitle: 'Blackboard', url: PAIN },
        { title: 'Exam templates', subtitle: 'Cervical spine, shoulder, hip, knee, lumbar spine and lower extremity. Blackboard', url: TEMPLATES },
      ] },
      ...vaOr.documents.groups,
    ],
  },

  guides: {
    clinic: {
      title: 'Clinic & OR Days',
      sections: [
        { title: 'Clinic day', blocks: [{ type: 'steps', items: [
          'Arrive 08:00. Check who has checked in; chart-review the rest. Residents split the list',
          'See the patient: focused exam from the templates, review the imaging',
          'Call the attending with the history and exam. Agree the plan: intervention or conservative management',
          'CPRS pain consult note. iMed consent if a procedure is planned',
          'OR orders, labs and imaging as needed',
          'Usually done by early afternoon',
        ] }] },
        { title: 'Procedure day', blocks: [{ type: 'steps', items: [
          'SACC preop at 07:00 for the first patient',
          'IV only if the patient needs one. Your judgement',
          'Meet the attending and get your lead',
          'Procedures until the list is done, usually early afternoon',
        ] }] },
      ],
      sources: [{ title: 'VA chronic pain clinic orientation', href: PAIN, detail: 'Blackboard, sign-in required' }],
    },

    charting: {
      title: 'Charting & Consents',
      sections: [
        { title: 'Every encounter', blocks: [{ type: 'bullets', items: [
          'Billing code: initial or follow-up, and the complexity',
          'A diagnosis, and whether the condition is service connected',
        ] }] },
        { title: 'When a procedure is planned', blocks: [{ type: 'bullets', items: [
          'Pick the correct procedure on the iMed consent. Consents last two months',
          'Consent comment: "A member of the anesthesiology team may be present for anxiolysis if required."',
          'Delayed orders for SACC',
          'Add Sean Nguyen as an additional signer on the note and arrange preop screening with him',
        ] }] },
      ],
      sources: [{ title: 'VA chronic pain clinic orientation', href: PAIN, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Week',
      sections: [
        { title: 'Before you start', blocks: [{ type: 'bullets', items: [
          'The coordinator emails the consult templates with the exam manoeuvres. Learn to perform and interpret them',
          'Review spinal anatomy for epidural and facet injections',
        ] }] },
        { title: 'Day one', blocks: [{ type: 'bullets', items: [
          'Meet Sean Nguyen, clinic nurse, at 07:00 in 5C-222 by the green elevator. He sets up the CPRS templates and encounter context and shows you the clinic and the patient list',
        ] }] },
        { title: 'The week', blocks: [{ type: 'rows', rows: [
          { label: 'Monday', value: 'OR from 07:00' },
          { label: 'Tuesday', value: 'Follow-up clinic from 08:00', note: 'First patients 09:00' },
          { label: 'Wednesday', value: 'OR from 08:00', note: 'After Grand Rounds' },
          { label: 'Thursday', value: 'New patient clinic from 08:00' },
          { label: 'Friday', value: 'Follow-up clinic with Dr Yanosik from 08:00', note: 'Sometimes procedures' },
        ] }] },
      ],
      sources: [{ title: 'VA chronic pain clinic orientation', href: PAIN, detail: 'Blackboard, sign-in required' }],
    },
  },
};
